import {
  AppContext,
  Message,
  ModalComponent,
  Progress,
  RESTUtils,
  Translate,
} from '@galigeo-store/ui-lib';
import { publish, PUBSUB_TOPICS } from '../state/pubsub';
import { html } from 'lit';
import '../components/modal/map-modal.component';
import '../components/modal/add-map-modal.component';
import { MenuService } from '../services/menu-service';
import { PortalAppContext } from '../state/portal-appcontext';
import { GaligeoCloudService } from '../services/galigeo-cloud.service';

export class MapModalController {
  private static modal: ModalComponent;

  public static init() {
    console.log('MapModalController initialized');
  }

  static async openMapModal(opts: {
    editionEnabled?: boolean;
    categoriesEditable: boolean;
    category?: string | null;
    label?: string;
    oldCategory?: string;
  }) {
    console.log('Opening map modal');
    const menu = await MenuService.getMenu();

    const maps = await GaligeoCloudService.getMaps({ updateCategories: true, menu });
    for (const element of menu) {
      if (element.maps) {
        element.maps = element.maps.filter((map: any) =>
          maps.some((m: any) => m.id === map.id),
        );
      }
    }

    const user = PortalAppContext.getCurrentUser();
    const editionEnabled = user.capabilities?.includes('author');

    const initialCategory = typeof opts.category === 'undefined' ? null : opts.category;

    const handleModalAction = async (e: CustomEvent) => {
      console.log('Closing map modal', e.detail.map);
      if (e.detail.closeModal) {
        MapModalController.modal.close();
      }
      // Prevent the modal from closing automatically during processing
      MapModalController.modal.autoClose = false;
      if (e.detail.action === 'add') {
        await MapModalController.addMap(e.detail.category);
      } else if (e.detail.action === 'delete') {
        await MapModalController.deleteMap(e.detail.map);
      } else if (e.detail.action === 'rights') {
        await MapModalController.manageRights(e.detail.map);
      } else if (e.detail.action === 'edit_name') {
        await MapModalController.editName(e.detail.map);
      } else if (e.detail.action === 'duplicate') {
        await MapModalController.duplicateMap(e.detail.map);
      } else {
        publish(PUBSUB_TOPICS.SHOW_CONTENT, { map: e.detail.map, newTab: e.detail.newTab });
      }
    };

    let modalTitle = opts.label ?? Translate.get('others_title');
    if (initialCategory !== null && initialCategory !== undefined) {
      const allCategories = await MenuService.getCategories();
      let selectedCat = allCategories.find((c: any) => c.id === initialCategory);
      if (!selectedCat && initialCategory === null) {
        selectedCat = { label: Translate.get('uncategorized') };
      }
      if (selectedCat) {
        modalTitle = `${selectedCat.label}`;
      }
    }

    // Crée le modal et stocke la référence
    MapModalController.modal = Message.createModal(
      html`<ggo-modal
        modalTitle="${modalTitle}"
        variant="list"
        .showValidateBtn="${false}"
        .showCloseBtn="${true}"
        .showCancelBtn="${false}"
        @cancel="${() => this.closeMapModal(opts.oldCategory)}"
        ><menu-modal
          style="height: calc(100vh - 400px);"
          .categoriesEditable="${opts.categoriesEditable}"
          .editionEnabled="${editionEnabled}"
          .category="${initialCategory}"
          .menu="${menu}"
          ._allMaps="${maps}"
          @map-clicked="${handleModalAction}"
          @changed="${async (e: CustomEvent) => {
          const newCatId = e.detail?.id;
          let newTitle = opts.label ?? Translate.get('others_title');
          if (newCatId !== null) {
            newTitle = `${Translate.get('filter_not_null')}`;
          }
          const modalEl = document.querySelector('ggo-modal');
          if (modalEl) {
            modalEl.setAttribute('modalTitle', newTitle);
          }
        }}"
        ></menu-modal
      ></ggo-modal>`,
    ).open();
  }

  static closeMapModal(category?: string | null) {
    console.log('Closing map modal');
    publish(PUBSUB_TOPICS.REFRESH_LEFT_PANEL, { resetActiveElement: category });
    // Logic to close the map modal
  }

  static async addMap(category: string) {
    MapModalController.modal.close();
    const newMap = {
      id: 'new',
      name: '',
      type: 'map',
    };

    const addModal = Message.createModal(
      html`<ggo-modal
        modalTitle="${Translate.get('add_modal_title')}"
        variant="list"
        .showValidateBtn="${false}"
        .showAddBtn="${true}"
        .autoClose="${false}"
      >
        <add-map-modal .newMap="${newMap}"></add-map-modal>
      </ggo-modal>`,
    ).open();

    addModal.addEventListener('save', async (e: Event) => {
      const customEvent = e as CustomEvent;
      console.log('Add map modal event:', customEvent.detail);

      const mapInfo = await GaligeoCloudService.addMap(newMap);

      if (category) {
        // If category is selected, add the map to the category
        const menu = await MenuService.getMenu();
        mapInfo.categoryId = category;
        menu.forEach((item: any) => {
          if (item.id === category) {
            item.maps.push(mapInfo);
          }
        });
        MenuService.saveMenu(menu);
      }
      publish(PUBSUB_TOPICS.SHOW_CONTENT, { map: mapInfo });
    });
  }

  static async deleteMap(map: any) {
    console.log('Deleting map:', map);
    const ok = await Message.confirm(
      Translate.get('map_modal_delete_map_confirm'),
    );
    if (ok) {
      Progress.start(Translate.get('map_modal_delete_map_progress'));
      //MapModalController.sendEvent('wait');
      await GaligeoCloudService.deleteMap(map.id);
      MapModalController.sendEvent('refresh');
      Progress.stop();
    }
  }

  static async manageRights(map: any) {
    const user = PortalAppContext.getCurrentUser();
    let rightsUrl = map.url.replace('index.html', `core/#/configuration/share`);
    if (map.type === 'application') {
      rightsUrl =
        AppContext.getCurrentUser().platformUrl +
        '/viewer/core/#/configuration/share?url=' +
        AppContext.getCurrentUser().platformUrl +
        '/feature/rest/services/app_' +
        map.id +
        '/FeatureServer';
    }
    rightsUrl = RESTUtils.setUrlParameter(rightsUrl, 'lang', user.lang);
    let checkCredentialsUrl =
      PortalAppContext.getCurrentUser().platformUrl +
      '/viewer/checkCredentials.html?redirect=' +
      encodeURIComponent(rightsUrl);
    checkCredentialsUrl = RESTUtils.setUrlParameter(
      checkCredentialsUrl,
      'ggo_token',
      RESTUtils.getToken(),
    );
    window.open(checkCredentialsUrl, '_blank');
  }

  static async editName(map: any) {
    console.log('Editing name:', map);
    const twoInputsPromise = new Promise<{
      label: string;
      description: string;
      url?: string;
    }>((resolve, reject) => {
      const isMap = map.type === 'map';
      const modal = Message.createModal(
        html`<ggo-modal
          variant="list"
          .showValidateBtn="${true}"
          .showCancelBtn="${true}"
          .autoClose="${false}"
          modalTitle="${Translate.get('map_modal_edit_name')}"
          @save=${() => {
            const labelInput =
              (
                document.getElementById('mapName') as HTMLInputElement
              )?.value?.trim() ?? '';
            const descInput =
              (
                document.getElementById('mapDescription') as HTMLInputElement
              )?.value?.trim() ?? '';
            if (isMap) {
              resolve({ label: labelInput, description: descInput });
            } else {
              const urlInput =
                (
                  document.getElementById('mapUrl') as HTMLInputElement
                )?.value?.trim() ?? '';
              resolve({
                label: labelInput,
                description: descInput,
                url: urlInput,
              });
            }
            modal.close();
          }}
          @cancel=${() => {
            reject();
            modal.close();
          }}
        >
          <div class="slds-form-element">
            <label for="mapName">${Translate.get('map_modal_edit_name')}</label>
            <input
              type="text"
              id="mapName"
              placeholder="${Translate.get('add_enter_map_name')}"
              value="${map.label}"
              class="slds-input"
              style="width: 100%; margin-bottom: 1em;"
            />
          </div>
          <div class="slds-form-element">
            <label for="mapDescription"
              >${Translate.get('map_modal_edit_description')}</label
            >
            <input
              type="text"
              id="mapDescription"
              placeholder="${Translate.get('map_modal_edit_description')}"
              value="${map.description || ''}"
              class="slds-input"
              style="width: 100%"
            />
          </div>
          ${!isMap
            ? html` <div class="slds-form-element" style="margin-top: 1em;">
                <label for="mapUrl">${Translate.get('map_modal_edit_url')}</label>
                <input
                  type="text"
                  id="mapUrl"
                  placeholder="${Translate.get('map_modal_edit_url')}"
                  value="${map.url || ''}"
                  class="slds-input"
                  style="width: 100%"
                />
              </div>`
            : ''}
        </ggo-modal>`,
      );
      modal.open();
    });

    try {
      const { label, description, url } = await twoInputsPromise;
      if ((!label || label === map.label.trim()) && (!url || url === map.url) && (description === map.description || (!description && !map.description))) {
        return;
      }
      const data: { label: string; description: string; url?: string; } = {
        label,
        description,
      };
      if (map.type !== 'map') {
        data.url = url;
        await GaligeoCloudService.editApplication(map.id, data);
      } else {
        await GaligeoCloudService.renameDocument(map.id, data);
      }
      MapModalController.sendEvent('refresh');
    } catch {
      return;
    }
  }

  private static buildDuplicateLabel(sourceLabel: string, existingLabels: Set<string>): string {
    const match = sourceLabel.match(/^(.*) \((\d+)\)$/);
    const base = match ? match[1] : sourceLabel;
    let n = match ? Number(match[2]) + 1 : 1;
    while (existingLabels.has(`${base} (${n})`)) {
      n++;
    }
    return `${base} (${n})`;
  }

  static async duplicateMap(map: any) {
    const existingMaps = await GaligeoCloudService.getMaps();
    const existingLabels = new Set<string>(existingMaps.map((m: any) => m.label));
    const defaultLabel = MapModalController.buildDuplicateLabel(map.label, existingLabels);

    const inputsPromise = new Promise<{ label: string; copySectorization: boolean }>((resolve, reject) => {
      const modal = Message.createModal(
        html`<ggo-modal
          variant="list"
          .showValidateBtn="${true}"
          .showCancelBtn="${true}"
          .autoClose="${false}"
          modalTitle="${Translate.get('map_modal_duplicate_map')}"
          @save=${() => {
            const labelInput =
              (document.getElementById('duplicateMapName') as HTMLInputElement)?.value?.trim() ?? '';
            const copyInput =
              (document.getElementById('duplicateCopySectorization') as HTMLInputElement)?.checked ?? true;
            resolve({ label: labelInput, copySectorization: copyInput });
            modal.close();
          }}
          @cancel=${() => {
            reject();
            modal.close();
          }}
        >
          <div class="slds-form-element">
            <label class="slds-form-element__label" for="duplicateMapName"
              >${Translate.get('map_modal_duplicate')}</label
            >
            <input
              type="text"
              id="duplicateMapName"
              value="${defaultLabel}"
              class="slds-input"
              style="width: 100%; margin-bottom: 1em;"
            />
          </div>
          <div class="slds-form-element">
            <div class="slds-form-element__control">
              <div class="slds-checkbox">
                <input
                  type="checkbox"
                  id="duplicateCopySectorization"
                  checked
                />
                <label class="slds-checkbox__label" for="duplicateCopySectorization">
                  <span class="slds-checkbox_faux"></span>
                  <span class="slds-form-element__label"
                    >${Translate.get('map_modal_duplicate_copy_sectorization')}</span
                  >
                </label>
              </div>
            </div>
          </div>
        </ggo-modal>`,
      );
      modal.open();
    });

    let newLabel: string;
    let copySectorization: boolean;
    try {
      const result = await inputsPromise;
      newLabel = result.label;
      copySectorization = result.copySectorization;
    } catch {
      return;
    }

    if (!newLabel) {
      return;
    }

    if (existingLabels.has(newLabel)) {
      Message.alert(Translate.get('map_modal_duplicate_name_exists'));
      return;
    }

    Progress.start(Translate.get('map_modal_duplicate_progress'));
    const newMap = await GaligeoCloudService.duplicateMap(
      map.id,
      newLabel,
      copySectorization,
    );
    MapModalController.modal.close();
    publish(PUBSUB_TOPICS.SHOW_CONTENT, { map: newMap });
    Progress.stop();
  }

  static sendEvent(eventName: string) {
    const menuModal = document.querySelector('menu-modal');
    if (menuModal) {
      menuModal.dispatchEvent(
        new CustomEvent(eventName, {
          detail: {},
        }),
      );
    } else {
      console.warn('MenuModal not found for event:', eventName);
    }
    // Recactivate the autoClose after sending the event
    MapModalController.modal.autoClose = true;
  }
}
